import type { UIMessage, ToolUIPart, DynamicToolUIPart } from 'ai';
import { coco } from '@cotera/api';
import {
  COTERA_ARTIFACT_DATA_KEY,
  COTERA_ARTIFACT_ID_KEY,
  COTERA_ARTIFACT_KEY,
  COTERA_ARTIFACT_ORIGIN_KEY,
  COTERA_ARTIFACT_TYPE_KEY,
  type ArtifactOrigin,
} from './artifact-constants';
import { ToolKey } from '../tool-key';
import type { ChatAction, ToolApprovalAction } from './chat-action';
import {
  isTipTapTextNode,
  type ContentNode,
  type DocumentState,
  type TextNode,
} from '../dg/document/document-types';
import { getToolPendingAction, ToolPendingAction } from './tool-incomplete';
import { extractToolImageEnvelope } from './tool-image';
import { parseIngotToolReceipt, type IngotToolReceipt } from './ingot-receipt';

const { isWorkflowToolKey } = coco;

/**
 * Raw part from UIMessage.parts (ai SDK) or our custom editor-state part.
 * Used as input to hydratePart().
 */
export type RawMessagePart =
  | UIMessage['parts'][number]
  | EditorStateUIPart
  | StoredSystemMessageUIPart;

/**
 * Cotera-specific UI part for rich user messages (editor state / document graph).
 */
export type EditorStateUIPart = {
  type: 'editor-state';
  editorState: DocumentState;
};

/** Compact persisted conversation system row (coco / ad-hoc), expanded at LLM build time. */
export type StoredSystemMessageUIPart = {
  type: 'stored-system-message';
  payload: { t: 'coco' } | { t: 'ad-hoc'; message: string };
};

/**
 * Union of all hydrated message part classes.
 * Use type guards (e.g. part.type === 'text', part instanceof TextPart) to narrow.
 */
export type MessagePart =
  | TextPart
  | ReasoningPart
  | SourceUrlPart
  | SourceDocumentPart
  | FilePart
  | DataPart
  | StepStartPart
  | EditorStatePart
  | StoredSystemMessagePart
  | ToolPart
  | UnknownPart;

/**
 * Base interface for hydrated message parts.
 * Each part wraps a raw UIMessage part (or EditorStateUIPart) and exposes a stable type and raw accessor.
 */
export interface MessagePartBase<TRaw = RawMessagePart> {
  readonly type: string;
  raw(): TRaw;
  toText(): string;
}

/**
 * Abstract base for hydrated message parts.
 * Provides type guard methods for narrowing MessagePart; each subclass overrides its own guard to return true.
 */
export abstract class BaseMessagePart
  implements MessagePartBase<RawMessagePart>
{
  abstract readonly type: string;
  abstract raw(): RawMessagePart;

  toText(): string {
    return '';
  }

  isTextPart(): this is TextPart {
    return false;
  }
  isReasoningPart(): this is ReasoningPart {
    return false;
  }
  isSourceUrlPart(): this is SourceUrlPart {
    return false;
  }
  isSourceDocumentPart(): this is SourceDocumentPart {
    return false;
  }
  isFilePart(): this is FilePart {
    return false;
  }
  isDataPart(): this is DataPart {
    return false;
  }
  isStepStartPart(): this is StepStartPart {
    return false;
  }
  isEditorStatePart(): this is EditorStatePart {
    return false;
  }
  isStoredSystemMessagePart(): this is StoredSystemMessagePart {
    return false;
  }
  isToolPart(): this is ToolPart {
    return false;
  }
  isWorkflowToolCall(): this is WorkflowToolCallPart {
    return false;
  }
  isUnknownPart(): this is UnknownPart {
    return false;
  }
}

// Raw part shapes (match ai SDK UIMessagePart variants) for type guards
type TextUIPartLike = {
  type: 'text';
  text: string;
  state?: 'streaming' | 'done';
};
type ReasoningUIPartLike = {
  type: 'reasoning';
  text: string;
  state?: 'streaming' | 'done';
};
type SourceUrlUIPartLike = {
  type: 'source-url';
  sourceId: string;
  url: string;
  title?: string;
};
type SourceDocumentUIPartLike = {
  type: 'source-document';
  sourceId: string;
  mediaType: string;
  title: string;
  filename?: string;
};
type FileUIPartLike = {
  type: 'file';
  mediaType: string;
  filename?: string;
  url: string;
};
type StepStartUIPartLike = { type: 'step-start' };
type DataUIPartLike = { type: `data-${string}`; id?: string; data: unknown };

function isToolRawPart(
  part: RawMessagePart
): part is ToolUIPart | DynamicToolUIPart {
  return (
    typeof (part as { type?: string }).type === 'string' &&
    ((part as { type: string }).type.startsWith('tool-') ||
      (part as { type: string }).type === 'dynamic-tool')
  );
}

function isEditorStateRawPart(part: RawMessagePart): part is EditorStateUIPart {
  const p = part as { type?: string; editorState?: unknown };
  return p.type === 'editor-state' && 'editorState' in p;
}

function isStoredSystemMessageRawPart(
  part: RawMessagePart
): part is StoredSystemMessageUIPart {
  const p = part as { type?: string; payload?: unknown };
  return p.type === 'stored-system-message' && 'payload' in p;
}

function isDataRawPart(
  part: RawMessagePart
): part is UIMessage['parts'][number] & { type: `data-${string}` } {
  return (
    typeof (part as { type?: string }).type === 'string' &&
    (part as { type: string }).type.startsWith('data-')
  );
}

function isTextRawPart(part: RawMessagePart): part is TextUIPartLike {
  const p = part as { type?: string };
  return p.type === 'text';
}
function isReasoningRawPart(part: RawMessagePart): part is ReasoningUIPartLike {
  return (part as { type?: string }).type === 'reasoning';
}
function isSourceUrlRawPart(part: RawMessagePart): part is SourceUrlUIPartLike {
  return (part as { type?: string }).type === 'source-url';
}
function isSourceDocumentRawPart(
  part: RawMessagePart
): part is SourceDocumentUIPartLike {
  return (part as { type?: string }).type === 'source-document';
}
function isFileRawPart(part: RawMessagePart): part is FileUIPartLike {
  return (part as { type?: string }).type === 'file';
}

const IMAGE_FILE_MEDIA_TYPE_PREFIX = 'image/';

/**
 * Whether a raw UI part represents an image attachment (native `image` part or
 * `file` with an image media type).
 */
export function rawMessagePartIsImage(part: RawMessagePart): boolean {
  if ((part as { type?: string }).type === 'image') {
    return true;
  }
  if (!isFileRawPart(part)) {
    return false;
  }
  return part.mediaType.startsWith(IMAGE_FILE_MEDIA_TYPE_PREFIX);
}

/** Whether a user {@link UIMessage} contains any image parts. */
export function uiMessageHasImageParts(uiMessage: UIMessage): boolean {
  if (uiMessage.role !== 'user') {
    return false;
  }
  const parts = uiMessage.parts ?? [];
  return parts.some((part) => rawMessagePartIsImage(part));
}

/**
 * Whether a {@link UIMessage} carries an image returned by a tool (a
 * `toolImage(...)` envelope in a tool part's output). Tool parts live on
 * assistant messages, so this is checked separately from
 * {@link uiMessageHasImageParts}. Used so a tool-returned image still trips the
 * model's vision-support guard even though it isn't a user attachment.
 */
export function uiMessageHasToolImageParts(uiMessage: UIMessage): boolean {
  const parts = uiMessage.parts ?? [];
  return parts.some(
    (part) =>
      isToolRawPart(part) &&
      extractToolImageEnvelope((part as { output?: unknown }).output) !== null
  );
}

function isStepStartRawPart(part: RawMessagePart): part is StepStartUIPartLike {
  return (part as { type?: string }).type === 'step-start';
}

/**
 * Hydrate a raw message part into the corresponding part class.
 * Tool parts require actions for approval status; other parts do not.
 */
export function createToolPart(
  part: ToolUIPart | DynamicToolUIPart,
  actions: ChatAction[] = []
): ToolPart {
  if (isWorkflowToolRawPart(part)) {
    return new WorkflowToolCallPart(part, actions);
  }
  return new ToolPart(part, actions);
}

export function hydratePart(
  part: RawMessagePart,
  actions: ChatAction[] = []
): MessagePart {
  if (isToolRawPart(part)) {
    return createToolPart(part, actions);
  }
  if (isEditorStateRawPart(part)) {
    return new EditorStatePart(part);
  }
  if (isStoredSystemMessageRawPart(part)) {
    return new StoredSystemMessagePart(part);
  }
  if (isDataRawPart(part)) {
    return new DataPart(part);
  }
  if (isTextRawPart(part)) {
    return new TextPart(part);
  }
  if (isReasoningRawPart(part)) {
    return new ReasoningPart(part);
  }
  if (isSourceUrlRawPart(part)) {
    return new SourceUrlPart(part);
  }
  if (isSourceDocumentRawPart(part)) {
    return new SourceDocumentPart(part);
  }
  if (isFileRawPart(part)) {
    return new FilePart(part);
  }
  if (isStepStartRawPart(part)) {
    return new StepStartPart(part);
  }
  return new UnknownPart(part as UIMessage['parts'][number]);
}

// --- Hydrated part classes (matching UIMessagePart variants) ---

export class TextPart
  extends BaseMessagePart
  implements MessagePartBase<TextUIPartLike>
{
  readonly type = 'text' as const;
  private readonly _raw: TextUIPartLike;

  constructor(raw: TextUIPartLike) {
    super();
    this._raw = raw;
  }

  override isTextPart(): this is TextPart {
    return true;
  }

  get text(): string {
    return this._raw.text;
  }

  get state(): 'streaming' | 'done' | undefined {
    return this._raw.state;
  }

  override toText(): string {
    return this.text;
  }

  raw(): TextUIPartLike {
    return this._raw;
  }
}

export class ReasoningPart
  extends BaseMessagePart
  implements MessagePartBase<ReasoningUIPartLike>
{
  readonly type = 'reasoning' as const;
  private readonly _raw: ReasoningUIPartLike;

  constructor(raw: ReasoningUIPartLike) {
    super();
    this._raw = raw;
  }

  override isReasoningPart(): this is ReasoningPart {
    return true;
  }

  get text(): string {
    return this._raw.text;
  }

  get state(): 'streaming' | 'done' | undefined {
    return this._raw.state;
  }

  raw(): ReasoningUIPartLike {
    return this._raw;
  }
}

export class SourceUrlPart
  extends BaseMessagePart
  implements MessagePartBase<SourceUrlUIPartLike>
{
  readonly type = 'source-url' as const;
  private readonly _raw: SourceUrlUIPartLike;

  constructor(raw: SourceUrlUIPartLike) {
    super();
    this._raw = raw;
  }

  override isSourceUrlPart(): this is SourceUrlPart {
    return true;
  }

  get sourceId(): string {
    return this._raw.sourceId;
  }

  get url(): string {
    return this._raw.url;
  }

  get title(): string | undefined {
    return this._raw.title;
  }

  raw(): SourceUrlUIPartLike {
    return this._raw;
  }
}

export class SourceDocumentPart
  extends BaseMessagePart
  implements MessagePartBase<SourceDocumentUIPartLike>
{
  readonly type = 'source-document' as const;
  private readonly _raw: SourceDocumentUIPartLike;

  constructor(raw: SourceDocumentUIPartLike) {
    super();
    this._raw = raw;
  }

  override isSourceDocumentPart(): this is SourceDocumentPart {
    return true;
  }

  get sourceId(): string {
    return this._raw.sourceId;
  }

  get mediaType(): string {
    return this._raw.mediaType;
  }

  get title(): string {
    return this._raw.title;
  }

  get filename(): string | undefined {
    return this._raw.filename;
  }

  raw(): SourceDocumentUIPartLike {
    return this._raw;
  }
}

export class FilePart
  extends BaseMessagePart
  implements MessagePartBase<FileUIPartLike>
{
  readonly type = 'file' as const;
  private readonly _raw: FileUIPartLike;

  constructor(raw: FileUIPartLike) {
    super();
    this._raw = raw;
  }

  override isFilePart(): this is FilePart {
    return true;
  }

  get mediaType(): string {
    return this._raw.mediaType;
  }

  get filename(): string | undefined {
    return this._raw.filename;
  }

  get url(): string {
    return this._raw.url;
  }

  raw(): FileUIPartLike {
    return this._raw;
  }
}

export class DataPart
  extends BaseMessagePart
  implements MessagePartBase<DataUIPartLike>
{
  private readonly _raw: DataUIPartLike;

  constructor(raw: DataUIPartLike) {
    super();
    this._raw = raw;
  }

  override get type(): `data-${string}` {
    return this._raw.type;
  }

  override isDataPart(): this is DataPart {
    return true;
  }

  /** Data part name (e.g. "mySchema" for type "data-mySchema"). */
  get dataName(): string {
    return this._raw.type.slice(5);
  }

  get id(): string | undefined {
    return this._raw.id;
  }

  get data(): unknown {
    return this._raw.data;
  }

  raw(): DataUIPartLike {
    return this._raw;
  }
}

export class StepStartPart
  extends BaseMessagePart
  implements MessagePartBase<StepStartUIPartLike>
{
  readonly type = 'step-start' as const;
  private readonly _raw: StepStartUIPartLike;

  constructor(raw: StepStartUIPartLike) {
    super();
    this._raw = raw;
  }

  override isStepStartPart(): this is StepStartPart {
    return true;
  }

  raw(): StepStartUIPartLike {
    return this._raw;
  }
}

type EditorStateTextNode = ContentNode | TextNode;

function editorStateNodeToText(node: EditorStateTextNode): string {
  if (isTipTapTextNode(node)) {
    return node.text;
  }

  const chunks: string[] = [];
  for (const child of node.content ?? []) {
    const childText = editorStateNodeToText(child);
    if (childText !== '') {
      chunks.push(childText);
    }
  }
  return chunks.join(' ');
}

function editorStateToText(editorState: DocumentState): string {
  const chunks: string[] = [];
  for (const child of editorState.content ?? []) {
    const childText = editorStateNodeToText(child);
    if (childText !== '') {
      chunks.push(childText);
    }
  }
  return chunks.join('\n');
}

export class EditorStatePart
  extends BaseMessagePart
  implements MessagePartBase<EditorStateUIPart>
{
  readonly type = 'editor-state' as const;
  private readonly _raw: EditorStateUIPart;

  constructor(raw: EditorStateUIPart) {
    super();
    this._raw = raw;
  }

  override isEditorStatePart(): this is EditorStatePart {
    return true;
  }

  get editorState(): DocumentState {
    return this._raw.editorState;
  }

  override toText(): string {
    return editorStateToText(this.editorState);
  }

  raw(): EditorStateUIPart {
    return this._raw;
  }
}

export class StoredSystemMessagePart
  extends BaseMessagePart
  implements MessagePartBase<StoredSystemMessageUIPart>
{
  readonly type = 'stored-system-message' as const;
  private readonly _raw: StoredSystemMessageUIPart;

  constructor(raw: StoredSystemMessageUIPart) {
    super();
    this._raw = raw;
  }

  override isStoredSystemMessagePart(): this is StoredSystemMessagePart {
    return true;
  }

  get payload(): StoredSystemMessageUIPart['payload'] {
    return this._raw.payload;
  }

  raw(): StoredSystemMessageUIPart {
    return this._raw;
  }
}

/**
 * Parsed artifact data from a tool output that conforms to the artifact convention.
 * Used when tool returns { [COTERA_ARTIFACT_KEY]: { artifactType, artifactId?, data } }.
 */
export type ArtifactData = {
  artifactType: string;
  artifactId?: string;
  data: unknown;
  /**
   * What the producing tool said it did to this artifact.
   *
   * Optional only for transcripts: every `artifactResponse` written since this
   * key existed carries one, but messages persisted before it do not, and a
   * `load_artifact` action never had a tool to ask. Absent means "not stated",
   * which the chat reports as no verb rather than guessing one.
   */
  origin?: ArtifactOrigin;
};

/**
 * Tool layer sometimes completes with a single `{ error: string }` payload (e.g.
 * bad-request paths that return an error object instead of throwing).
 */
function isToolFailureErrorEnvelope(output: unknown): boolean {
  if (typeof output !== 'object' || output === null) {
    return false;
  }
  const keys = Object.keys(output as Record<string, unknown>);
  if (keys.length !== 1 || keys[0] !== 'error') {
    return false;
  }
  const err = (output as { error?: unknown }).error;
  return typeof err === 'string' && err.length > 0;
}

/**
 * Tool part class that wraps a ToolUIPart or DynamicToolUIPart.
 * Provides methods for checking approval status and other tool part operations.
 */
export class ToolPart
  extends BaseMessagePart
  implements MessagePartBase<ToolUIPart | DynamicToolUIPart>
{
  readonly type = 'tool' as const;
  private readonly _toolPart: ToolUIPart | DynamicToolUIPart;
  private readonly _actions: ChatAction[];
  // `_toolPart` is readonly, so the derived key never changes — memoize it. This
  // part is re-rendered O(3x) per ToolPartComponent and toolKey() runs regex
  // normalization each call. WorkflowToolCallPart inherits this unchanged.
  private _toolKeyCache: ToolKey | null = null;

  constructor(
    toolPart: ToolUIPart | DynamicToolUIPart,
    actions: ChatAction[] = []
  ) {
    super();
    this._toolPart = toolPart;
    this._actions = actions;
  }

  override isToolPart(): this is ToolPart {
    return true;
  }

  override isWorkflowToolCall(): this is WorkflowToolCallPart {
    return this instanceof WorkflowToolCallPart;
  }

  toolKey(): ToolKey {
    if (this._toolKeyCache === null) {
      this._toolKeyCache =
        this._toolPart.type === 'dynamic-tool'
          ? ToolKey.fromLlmName(this._toolPart.toolName)
          : ToolKey.fromLlmName(this._toolPart.type.replace('tool-', ''));
    }
    return this._toolKeyCache;
  }

  get toolCallId(): string {
    return this._toolPart.toolCallId;
  }

  get input(): Record<string, unknown> {
    // During the `input-streaming` state the raw part's input can still be
    // undefined, despite what the AI SDK types suggest.
    return (this._toolPart.input ?? {}) as Record<string, unknown>;
  }

  /** Underlying tool part (static or dynamic). */
  get toolPart(): ToolUIPart | DynamicToolUIPart {
    return this._toolPart;
  }

  /** Whether this is a dynamic tool (unknown at compile time). */
  get isDynamicTool(): boolean {
    return this._toolPart.type === 'dynamic-tool';
  }

  getOutput<T extends Record<string, any>>(): T | undefined {
    return this._toolPart.output as T | undefined;
  }

  /** The Ingot receipt stored with this result, or null when it has none. */
  ingotReceipt(): IngotToolReceipt | null {
    const raw: { callProviderMetadata?: { cotera?: { ingot?: unknown } } } =
      this._toolPart;
    return parseIngotToolReceipt(raw.callProviderMetadata?.cotera?.ingot);
  }

  isDenied(): boolean {
    try {
      const output = this._toolPart.output;
      const deniedByOutput =
        output !== null &&
        output !== undefined &&
        typeof output === 'object' &&
        !Array.isArray(output) &&
        (output as Record<string, unknown>)['@@cotera-tool-state'] === 'denied';

      const raw = this.raw();
      const deniedByRawState =
        raw !== null &&
        typeof raw === 'object' &&
        'state' in raw &&
        (raw as { state?: unknown }).state === 'output-denied';

      return deniedByOutput || deniedByRawState;
    } catch {
      // Tool parts can be partially malformed in persisted/transformed payloads.
      // Denied checks should never crash rendering.
      return false;
    }
  }

  /**
   * True when the tool finished with an error (API failure, execution-error, or
   * single-key `{ error: string }` envelope). Distinct from {@link isDenied}.
   */
  isError(): boolean {
    try {
      if (this._toolPart.state === 'output-error') {
        return true;
      }
      const output = this._toolPart.output;
      if (isToolFailureErrorEnvelope(output)) {
        return true;
      }
      return (
        typeof output === 'object' &&
        output !== null &&
        'type' in output &&
        (output as { type?: string }).type === 'execution-error'
      );
    } catch {
      return false;
    }
  }

  /**
   * Returns parsed artifact data when output conforms to { [COTERA_ARTIFACT_KEY]: { artifactType, data } };
   * otherwise null. Uses a namespaced key to avoid accidental matches.
   */
  getArtifact(): ArtifactData | null {
    const output = this._toolPart.output;
    if (
      output === null ||
      output === undefined ||
      typeof output !== 'object' ||
      Array.isArray(output)
    ) {
      return null;
    }
    const o = output as Record<string, unknown>;
    const artifact = o[COTERA_ARTIFACT_KEY];
    if (
      artifact === null ||
      artifact === undefined ||
      typeof artifact !== 'object' ||
      Array.isArray(artifact)
    ) {
      return null;
    }
    const a = artifact as Record<string, unknown>;
    if (
      typeof a[COTERA_ARTIFACT_TYPE_KEY] !== 'string' ||
      a[COTERA_ARTIFACT_DATA_KEY] === undefined
    ) {
      return null;
    }
    const result: ArtifactData = {
      artifactType: a[COTERA_ARTIFACT_TYPE_KEY] as string,
      data: a[COTERA_ARTIFACT_DATA_KEY],
    };
    if (typeof a[COTERA_ARTIFACT_ID_KEY] === 'string') {
      result.artifactId = a[COTERA_ARTIFACT_ID_KEY] as string;
    }
    // Checked against the union rather than trusted: this is parsed from a
    // stored tool output, so an older message can carry no origin at all and a
    // hand-written one can carry anything.
    const origin = a[COTERA_ARTIFACT_ORIGIN_KEY];
    if (origin === 'created' || origin === 'edited' || origin === 'opened') {
      result.origin = origin;
    }
    return result;
  }

  /** True when part has output available (state === 'output-available'). */
  get hasOutput(): boolean {
    return this._toolPart.state === 'output-available';
  }

  /** True when part has output but is still streaming (preliminary). */
  get isStreaming(): boolean {
    const part = this._toolPart as { preliminary?: boolean };
    return this.hasOutput && part.preliminary === true;
  }

  /** True when part has output and is not streaming (complete). */
  get isComplete(): boolean {
    return this.hasOutput && !this.isStreaming;
  }

  isCompleted(): boolean {
    return this.isComplete && !this.isIncomplete();
  }

  /**
   * True when the tool has input but has never been executed (no output yet).
   * Distinct from isIncomplete(): tools that returned incomplete({}) have output and
   * needExecution() is false — they are waiting for user input, not re-execution.
   */
  needsExecution(): boolean {
    return (
      !this.hasOutput &&
      (this._toolPart.state === 'input-available' ||
        this._toolPart.state === 'input-streaming' ||
        this._toolPart.state === undefined)
    );
  }

  isIncomplete(): boolean {
    function checkOutputIsIncomplete(output: unknown | undefined): boolean {
      return (
        output != null &&
        (output as Record<string, unknown>)['@@cotera-tool-state'] ===
          'incomplete'
      );
    }

    // Incomplete if output is the incomplete marker (e.g. after tool returned incomplete()),
    // regardless of state - persisted messages may have state missing or wrong
    if (checkOutputIsIncomplete(this._toolPart.output)) {
      return true;
    }

    // output-available with preliminary=true means still streaming (incomplete)
    if (this.isStreaming) {
      return true;
    }

    return (
      this._toolPart.state === 'input-available' ||
      this._toolPart.state === 'input-streaming' ||
      (this._toolPart.state === 'output-available' &&
        checkOutputIsIncomplete(this._toolPart.output))
    );
  }

  /**
   * Get the approval status for this tool part.
   * Checks both ChatActions and the tool part metadata.
   *
   * @returns 'required' | 'allowed' | 'approved' | 'denied'
   */
  approvalStatus(): 'required' | 'allowed' | 'approved' | 'denied' {
    const toolCallId = this._toolPart.toolCallId;

    for (const action of this._actions) {
      if (action.type === 'tool_approval') {
        const toolApprovalAction = action as ToolApprovalAction;
        if (toolApprovalAction.getToolCallId() === toolCallId) {
          const status = action.status;
          if (status === 'approved') {
            return 'approved';
          }
          if (status === 'denied') {
            return 'denied';
          }
          if (status === 'pending') {
            return 'required';
          }
        }
      }
    }

    const partWithMetadata = this._toolPart as (
      | ToolUIPart
      | DynamicToolUIPart
    ) & {
      callProviderMetadata?: {
        cotera?: { requiresApproval: boolean };
      };
      state?: string;
      output?: unknown;
    };

    const requiresApproval =
      partWithMetadata.callProviderMetadata?.cotera?.requiresApproval === true;
    const isInputAvailable = partWithMetadata.state === 'input-available';
    const hasOutput = partWithMetadata.output !== undefined;

    if (!requiresApproval) {
      return 'allowed';
    }
    if (isInputAvailable && !hasOutput) {
      return 'required';
    }
    if (hasOutput) {
      return 'approved';
    }
    return 'allowed';
  }

  raw(): ToolUIPart | DynamicToolUIPart {
    return this._toolPart;
  }
}

export type WorkflowToolCallPhase =
  | 'awaiting-approval'
  | 'running'
  | 'completed'
  | 'failed'
  | 'error';

type WorkflowToolPayload = {
  wrapperRunId?: string | null;
  targetWorkflowId?: string;
  targetRunId?: string | null;
  status?: string | null;
  ok?: boolean | null;
  error?: string | null;
  workflowName?: string | null;
  '@@cotera-tool-state'?: string;
};

const TOOL_INCOMPLETE_INPUT_KEY = '@@cotera-tool-incomplete';

function normalizeToolKey(toolKey: string): string {
  return toolKey.replaceAll('_', '/');
}

function parseWorkflowToolPayload(raw: unknown): WorkflowToolPayload | null {
  if (raw === undefined || raw === null) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw) as WorkflowToolPayload;
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') {
    return raw as WorkflowToolPayload;
  }
  return null;
}

function readWorkflowString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function readWorkflowBoolean(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function isWorkflowToolRawPart(part: ToolUIPart | DynamicToolUIPart): boolean {
  const toolKey =
    part.type === 'dynamic-tool'
      ? part.toolName
      : part.type.replace(/^tool-/, '');
  return isWorkflowToolKey(normalizeToolKey(toolKey));
}

/**
 * {@link ToolPart} specialization for `coco/workflow-*` tool calls.
 */
export class WorkflowToolCallPart extends ToolPart {
  override isWorkflowToolCall(): this is WorkflowToolCallPart {
    return true;
  }

  get targetWorkflowId(): string | null {
    return readWorkflowString(this.mergedPayload.targetWorkflowId);
  }

  get targetRunId(): string | null {
    return readWorkflowString(this.mergedPayload.targetRunId);
  }

  get status(): string | null {
    return readWorkflowString(this.mergedPayload.status);
  }

  get ok(): boolean | null {
    return readWorkflowBoolean(this.mergedPayload.ok);
  }

  get error(): string | null {
    return readWorkflowString(this.mergedPayload.error);
  }

  get workflowName(): string | null {
    return readWorkflowString(this.mergedPayload.workflowName);
  }

  get phase(): WorkflowToolCallPhase {
    if (this.isError() || this.rawToolState === 'output-error') {
      return 'error';
    }

    if (
      getToolPendingAction(this.getOutput()) === ToolPendingAction.AwaitApproval
    ) {
      return 'awaiting-approval';
    }

    if (this.isIncomplete()) {
      return 'running';
    }

    if (this.ok === false) {
      return 'failed';
    }

    return 'completed';
  }

  private get rawToolState(): string | undefined {
    const raw = this.raw() as { state?: string };
    return raw.state;
  }

  private get mergedPayload(): WorkflowToolPayload {
    const fromOutput = parseWorkflowToolPayload(this.getOutput());
    const fromInput = parseWorkflowToolPayload(
      this.input[TOOL_INCOMPLETE_INPUT_KEY]
    );
    return {
      ...(fromInput ?? {}),
      ...(fromOutput ?? {}),
    };
  }
}

/**
 * Wrapper for message part types that do not have a dedicated hydrated class yet.
 * Preserves the raw part for future handling or passthrough.
 */
export class UnknownPart
  extends BaseMessagePart
  implements MessagePartBase<UIMessage['parts'][number]>
{
  private readonly _raw: UIMessage['parts'][number];

  constructor(raw: UIMessage['parts'][number]) {
    super();
    this._raw = raw;
  }

  override get type(): string {
    return typeof this._raw === 'object' &&
      this._raw !== null &&
      'type' in this._raw
      ? String((this._raw as { type: unknown }).type)
      : 'unknown';
  }

  override isUnknownPart(): this is UnknownPart {
    return true;
  }

  raw(): UIMessage['parts'][number] {
    return this._raw;
  }
}
